import { useQuery } from '@tanstack/react-query';
import { adminApi } from '@vmgate/common';
import type { DashboardStats } from '@vmgate/common';
import { LoadingScreen } from '../components/common/LoadingScreen';
import { ErrorState } from '../components/common/ErrorState';
import { StatCard } from '../components/common/StatCard';
import { StatusBadge } from '../components/common/StatusBadge';
import { formatDateTime, formatNumber, formatPercent } from '../utils/format';

export function DashboardPage() {
  const { data, isLoading, isError, refetch } = useQuery<DashboardStats>({
    queryKey: ['dashboard'],
    queryFn: () => adminApi.getDashboardStats()
  });

  if (isLoading) {
    return <LoadingScreen message="Loading dashboard..." />;
  }

  if (isError || !data) {
    return <ErrorState onRetry={() => refetch()} message="Unable to load dashboard metrics." />;
  }

  const {
    total_customers,
    total_devices,
    total_licenses,
    active_licenses,
    revoked_licenses,
    recent_validations,
    successful_validations,
    validation_success_rate,
    device_brands,
    recent_licenses,
    license_quota_total,
    license_quota_assigned,
    license_quota_available
  } = data;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <div className="grid-2">
        <StatCard
          title="Customers"
          value={formatNumber(total_customers)}
          subtitle={`${formatNumber(license_quota_total)} licenses allocated`}
          icon="👥"
        />
        <StatCard
          title="Devices"
          value={formatNumber(total_devices)}
          subtitle={`${formatNumber(active_licenses)} active licenses`}
          icon="📱"
          tone="success"
        />
        <StatCard
          title="Licenses Issued"
          value={formatNumber(total_licenses)}
          subtitle={`${formatNumber(revoked_licenses)} revoked overall`}
          icon="🔐"
        />
        <StatCard
          title="License Capacity"
          value={`${formatNumber(license_quota_available)} free`}
          subtitle={`${formatNumber(license_quota_assigned)} in use`}
          icon="⚖️"
          tone="warning"
        />
      </div>

      <div className="grid-2">
        <div className="surface surface-padding" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div>
            <div style={{ fontWeight: 600, fontSize: '1.1rem' }}>Validation Health</div>
            <div className="text-muted">Last 30 days</div>
          </div>
          <div style={{ display: 'flex', gap: '1.5rem' }}>
            <div>
              <div className="text-muted" style={{ fontSize: '0.85rem' }}>
                Total Validations
              </div>
              <div style={{ fontSize: '1.5rem', fontWeight: 600 }}>{formatNumber(recent_validations)}</div>
            </div>
            <div>
              <div className="text-muted" style={{ fontSize: '0.85rem' }}>
                Successful
              </div>
              <div style={{ fontSize: '1.5rem', fontWeight: 600 }}>{formatNumber(successful_validations)}</div>
            </div>
            <div>
              <div className="text-muted" style={{ fontSize: '0.85rem' }}>
                Success Rate
              </div>
              <div style={{ fontSize: '1.5rem', fontWeight: 600 }}>{formatPercent(validation_success_rate)}</div>
            </div>
          </div>
          <div>
            <div style={{ fontWeight: 600, marginBottom: '0.5rem' }}>Top Device Brands</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
              {device_brands.length === 0 && <div className="text-muted">No device data yet.</div>}
              {device_brands.map((brand) => (
                <div
                  key={brand.brand || 'unknown'}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '0.45rem 0.6rem',
                    borderRadius: '10px',
                    background: 'rgba(15, 23, 42, 0.04)'
                  }}
                >
                  <span>{brand.brand || 'Unknown'}</span>
                  <span>{formatNumber(brand.count)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="surface surface-padding" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div>
            <div style={{ fontWeight: 600, fontSize: '1.1rem' }}>Recent Licenses</div>
            <div className="text-muted">10 most recent license issuances</div>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>License ID</th>
                  <th>Customer</th>
                  <th>Status</th>
                  <th>Issued</th>
                </tr>
              </thead>
              <tbody>
                {recent_licenses.map((item) => (
                  <tr key={item.license_id}>
                    <td style={{ fontFamily: 'monospace' }}>{item.license_id}</td>
                    <td>{item.customer_name ?? item.customer_id}</td>
                    <td>
                      <StatusBadge status={item.status} />
                    </td>
                    <td>{formatDateTime(item.created_at)}</td>
                  </tr>
                ))}
                {recent_licenses.length === 0 && (
                  <tr>
                    <td colSpan={4} className="text-muted" style={{ padding: '1rem', textAlign: 'center' }}>
                      No license records yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

